import { formatISO } from "date-fns"
import PropTypes from "prop-types"
import React, { createContext, useMemo } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import { parseDateValue } from "src/main/Billing/helpers"

import { queryContactInvoices } from "src/api/Billing/Payments"

import { RailsArray, useQueryParamState } from "src/hooks/use_query_param_state"
import { useTracker } from "src/hooks/use_tracker"

import { calculateSummaryTotals } from "./helpers"

export const ContactInvoicesContext = createContext({})

const ContactInvoicesContextProvider = ({
  children,
  contactId,
  initialInvoices,
  printViewEnabled,
  contact,
  marina,
}) => {
  const tracker = useTracker()
  const { marinaSlug } = useMarina()

  const [statusFilters, setStatusFilters] = useQueryParamState("statuses", {
    type: RailsArray,
    defaultValue: [],
  })
  const [startDateString, setStartDateString] = useQueryParamState(
    "due_date_gteq",
    { type: String, defaultValue: "" }
  )
  const [endDateString, setEndDateString] = useQueryParamState(
    "due_date_lteq",
    { type: String, defaultValue: "" }
  )
  const [sortKey, setSortKey] = useQueryParamState("sort_by", {
    type: String,
    defaultValue: "",
  })
  const [sortDirection, setSortDirection] = useQueryParamState(
    "sort_direction",
    { type: String, defaultValue: "" }
  )

  const startDate = startDateString ? parseDateValue(startDateString) : null
  const endDate = endDateString ? parseDateValue(endDateString) : null

  const handleStartDateChange = (date) => {
    const newStartDateString = date
      ? formatISO(date, { representation: "date" })
      : ""
    setStartDateString(newStartDateString)

    if (newStartDateString) {
      tracker.trackEvent("customer_invoices_tab:due_date_filter_pressed", {
        start_date: newStartDateString,
        end_date: endDateString,
      })
    }
  }

  const handleEndDateChange = (date) => {
    const newEndDateString = date
      ? formatISO(date, { representation: "date" })
      : ""
    setEndDateString(newEndDateString)

    if (newEndDateString) {
      tracker.trackEvent("customer_invoices_tab:due_date_filter_pressed", {
        start_date: startDateString,
        end_date: newEndDateString,
      })
    }
  }

  const statusOptions = [
    { value: "past_due", label: "Overdue" },
    { value: "failed", label: "Failed" },
    { value: "settled", label: "Settled" },
    { value: "processing", label: "Processing" },
    { value: "disputed", label: "Disputed" },
    { value: "scheduled", label: "Scheduled" },
  ]

  const queryParams = useMemo(() => {
    const params = {
      contactId,
      marinaSlug,
    }

    if (statusFilters.length > 0) {
      params.statuses = statusFilters
    }

    // Convert date filters to ISO strings
    if (startDate) {
      params.dueDateGteq = formatISO(startDate, { representation: "date" })
    }
    if (endDate) {
      params.dueDateLteq = formatISO(endDate, { representation: "date" })
    }

    // Add sort parameters
    if (sortKey && sortKey !== "") {
      params.sortBy = sortKey
    }
    if (sortDirection && sortDirection !== "") {
      params.sortDirection = sortDirection
    }

    return params
  }, [
    contactId,
    marinaSlug,
    statusFilters,
    startDate,
    endDate,
    sortKey,
    sortDirection,
  ])

  const {
    data: invoices,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["contactInvoices", queryParams],
    queryFn: () => queryContactInvoices(queryParams),
    initialData: initialInvoices,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const handleFilterSelect = (filters, value) => {
    const newFilters = filters.includes(value)
      ? filters.filter((v) => v !== value)
      : [...filters, value]
    setStatusFilters(newFilters)

    if (newFilters.length > 0) {
      tracker.trackEvent("customer_invoices_tab:status_filter_pressed", {
        statuses: newFilters,
      })
    }
  }

  const handleClearFilters = () => {
    setStatusFilters([])
    setStartDateString("")
    setEndDateString("")
  }

  const handleSort = (key) => {
    const newSortState =
      sortKey === key
        ? sortDirection === "ASC"
          ? { sortKey: key, sortDirection: "DESC" }
          : { sortKey: null, sortDirection: null }
        : { sortKey: key, sortDirection: "ASC" }

    setSortDirection(newSortState.sortDirection)
    setSortKey(newSortState.sortKey)
  }

  const sortConfig = {
    sortDirection: sortDirection || null,
    sortKey: sortKey || null,
    onColumnSort: handleSort,
  }

  const summaryTotals = useMemo(
    () => calculateSummaryTotals(invoices),
    [invoices]
  )

  return (
    <ContactInvoicesContext.Provider
      value={{
        sortConfig,
        statusFilters,
        statusOptions,
        handleFilterSelect,
        startDate,
        endDate,
        handleStartDateChange,
        handleEndDateChange,
        handleClearFilters,
        isError,
        invoices,
        isFetching,
        printViewEnabled,
        contact,
        marina,
        summaryTotals,
      }}
    >
      {children}
    </ContactInvoicesContext.Provider>
  )
}

export const initialInvoicesProps = {
  id: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  balance: PropTypes.number.isRequired,
  dueDate: PropTypes.string.isRequired,
  settledAtLocalTime: PropTypes.string,
  displayStatus: PropTypes.string.isRequired,
  autoSettleLastError: PropTypes.string,
  nextAutoSettleTime: PropTypes.string,
  paymentMethodId: PropTypes.string,
  sourceId: PropTypes.string.isRequired,
  sourcePath: PropTypes.string.isRequired,
  sourceType: PropTypes.string.isRequired,
}

export const contactProps = {
  email: PropTypes.string,
  name: PropTypes.string.isRequired,
  addressLineOne: PropTypes.string,
  addressLineTwo: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  displayPhone: PropTypes.string,
}

export const marinaProps = {
  name: PropTypes.string.isRequired,
  displayPhone: PropTypes.string,
  address: PropTypes.shape({
    addressLineOne: PropTypes.string,
    addressLineTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
  }),
  logoUrl: PropTypes.string,
}

ContactInvoicesContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  contactId: PropTypes.string.isRequired,
  initialInvoices: PropTypes.arrayOf(PropTypes.shape(initialInvoicesProps))
    .isRequired,
  printViewEnabled: PropTypes.bool.isRequired,
  contact: PropTypes.shape(contactProps).isRequired,
  marina: PropTypes.shape(marinaProps).isRequired,
}
export default ContactInvoicesContextProvider
